import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTRPC } from "./trpc.tsx";

/**
 * Realtime, as cache invalidation.
 *
 * The server sends an event name and the ids it concerns; this turns that into
 * TanStack Query invalidations and lets Query refetch what is actually on
 * screen. Nothing is pushed into the cache directly.
 *
 * That is a deliberate constraint. A push-the-entity design has to answer
 * "what if the client is not allowed to see this field", "what if the events
 * arrive out of order", and "what if the client never had this query"; letting
 * Query refetch answers all three by construction, at the cost of one request.
 */

/** Ids a projected event may carry. */
interface RealtimeEvent {
	readonly jobId?: string;
	readonly businessId?: string;
}

export type ConnectionState = "connecting" | "open" | "closed";

export interface UseRealtimeOptions {
	/** Origin of the API. The stream lives at `<baseUrl>/api/events`. */
	readonly baseUrl: string;
	readonly enabled?: boolean;
}

export function useRealtime(options: UseRealtimeOptions): ConnectionState {
	const queryClient = useQueryClient();
	const trpc = useTRPC();
	const [state, setState] = React.useState<ConnectionState>("closed");

	const enabled = options.enabled ?? true;

	// The proxy is stable, but referencing it inside the effect would make the
	// effect depend on it; capturing the key builders once keeps the dependency
	// list honest and the subscription from churning.
	const keys = React.useMemo(
		() => ({
			jobs: trpc.jobs.pathKey(),
			businesses: trpc.businesses.pathKey(),
			session: trpc.session.queryKey(),
		}),
		[trpc],
	);

	React.useEffect(() => {
		if (!enabled) {
			setState("closed");
			return;
		}

		const url = `${options.baseUrl.replace(/\/+$/, "")}/api/events`;
		// `withCredentials` is what carries the session cookie cross-origin, which
		// is how the server decides which events this viewer may see.
		const source = new EventSource(url, { withCredentials: true });
		let connected = false;

		setState("connecting");

		const invalidate = (key: readonly unknown[]) => {
			void queryClient.invalidateQueries({ queryKey: key });
		};

		const onJobEvent = () => invalidate(keys.jobs);
		const onBusinessEvent = () => {
			invalidate(keys.businesses);
			// A listing's card shows its business, so a rename or an approval has to
			// reach the job queries too.
			invalidate(keys.jobs);
		};
		const onMembershipEvent = () => {
			invalidate(keys.businesses);
			// Membership decides what the UI offers, and that lives on the session.
			invalidate(keys.session);
		};

		const handlers: Record<string, (event: MessageEvent<string>) => void> = {
			ready: () => {
				// Notifications are not replayed. Refresh after a reconnect to catch
				// changes missed during a network gap or a server buffer overflow.
				if (connected) void queryClient.invalidateQueries();
				connected = true;
				setState("open");
			},

			"job.created": onJobEvent,
			"job.updated": onJobEvent,
			"job.published": onJobEvent,
			"job.closed": onJobEvent,
			"job.deleted": onJobEvent,

			"business.created": onBusinessEvent,
			"business.updated": onBusinessEvent,
			"business.approved": onBusinessEvent,
			"business.suspended": onBusinessEvent,

			"business.member.added": onMembershipEvent,
			"business.member.removed": onMembershipEvent,
		};

		for (const [type, handler] of Object.entries(handlers)) {
			source.addEventListener(type, handler as EventListener);
		}

		source.addEventListener("open", () => setState("open"));
		source.addEventListener("error", () => {
			// EventSource reconnects on its own; surfacing "connecting" keeps any
			// status indicator honest without a manual retry loop.
			setState(
				source.readyState === EventSource.CLOSED ? "closed" : "connecting",
			);
		});

		return () => {
			for (const [type, handler] of Object.entries(handlers)) {
				source.removeEventListener(type, handler as EventListener);
			}
			source.close();
			setState("closed");
		};
	}, [enabled, options.baseUrl, queryClient, keys]);

	return state;
}

/** Parses a projected event's ids. Exposed for tests and custom handlers. */
export function parseRealtimeEvent(event: MessageEvent<string>): RealtimeEvent {
	try {
		return JSON.parse(event.data) as RealtimeEvent;
	} catch {
		return {};
	}
}
